/**
 * Row ⇄ client-state mappers. Supabase uses snake_case + uuid + timestamptz;
 * our Zustand stores use camelCase + string ids (for backward compatibility
 * with the localStorage-only flow). This module owns the translation.
 */
import type {
  Workspace, Board, Group, Column, Item, View,
} from '@/stores/workspaceStore';

// ---------- Workspace ----------
export function rowToWorkspace(r: any): Workspace {
  return {
    id: r.id,
    name: r.name,
    icon: r.icon ?? '',
    color: r.color ?? '#A855F7',
  };
}
export function workspaceToRow(w: Workspace, ownerId: string) {
  return {
    id: w.id,
    name: w.name,
    slug: w.id,                    // simple slug = id; Supabase trigger allows this
    owner_id: ownerId,
    icon: w.icon,
    color: w.color,
  };
}

// ---------- Board ----------
export function rowToBoard(r: any): Board {
  return {
    id: r.id,
    workspaceId: r.workspace_id,
    name: r.name,
    icon: r.icon ?? '',
    color: r.color ?? 'from-neon-purple to-neon-blue',
    description: r.description ?? undefined,
    starred: false,                    // starred_items is handled separately
    createdAt: r.created_at,
    updatedAt: r.updated_at,
  };
}
export function boardToRow(b: Board) {
  return {
    id: b.id,
    workspace_id: b.workspaceId,
    name: b.name,
    icon: b.icon,
    color: b.color,
    description: b.description,
  };
}

// ---------- Group ----------
export function rowToGroup(r: any): Group {
  return {
    id: r.id,
    boardId: r.board_id,
    name: r.name,
    color: r.color,
    collapsed: r.collapsed,
    position: r.position,
  };
}
export function groupToRow(g: Group) {
  return {
    id: g.id,
    board_id: g.boardId,
    name: g.name,
    color: g.color,
    position: g.position,
    collapsed: g.collapsed,
  };
}

// ---------- Column ----------
export function rowToColumn(r: any): Column {
  return {
    id: r.id,
    boardId: r.board_id,
    name: r.name,
    type: r.type,
    width: r.width,
    position: r.position,
    settings: r.settings ?? {},
  };
}
export function columnToRow(c: Column) {
  return {
    id: c.id,
    board_id: c.boardId,
    name: c.name,
    type: c.type,
    position: c.position,
    width: c.width,
    settings: c.settings ?? {},
  };
}

// ---------- Item ----------
export function rowToItem(r: any): Item {
  return {
    id: r.id,
    boardId: r.board_id,
    groupId: r.group_id,
    name: r.name,
    position: r.position,
    values: r.values ?? {},
    parentItemId: r.parent_item_id ?? undefined,
    createdAt: r.created_at,
    updatedAt: r.updated_at,
  };
}
export function itemToRow(it: Item, createdBy: string) {
  return {
    id: it.id,
    board_id: it.boardId,
    group_id: it.groupId,
    name: it.name,
    position: it.position,
    values: it.values ?? {},
    parent_item_id: it.parentItemId ?? null,
    created_by: createdBy,
  };
}

// ---------- View ----------
export function rowToView(r: any): View {
  return {
    id: r.id,
    boardId: r.board_id,
    name: r.name,
    type: r.type,
    isDefault: r.is_default,
    settings: r.settings ?? {},
  };
}
export function viewToRow(v: View) {
  return {
    id: v.id,
    board_id: v.boardId,
    name: v.name,
    type: v.type,
    is_default: v.isDefault,
    settings: v.settings ?? {},
  };
}
